import { useCallback } from 'react';

import type { CityFile } from '../../shared/cityFile';
import type { Opportunity } from '../../shared/opportunity';
import type { HiddenEvents } from '../../shared/useHiddenEvents';
import type { Rsvp } from '../../shared/useRsvp';
import { backupJson, makeBackup, mergeBackup, type Backup } from './backup';
import { buildIcs, downloadFile, downloadIcs } from './ics';
import ScheduleMenu from './ScheduleMenu';

/**
 * Saved's menu — the calendar file, the backup and its restore, Clear — wired
 * to the browser's own lists. It is the overflow of the Share my calendar
 * dialog (contract §18), the one way to take Saved elsewhere, and works on everything marked Going or Maybe in this city
 * whatever the filters or the layout show: a calendar file of "the ones in
 * view" would be a surprise the day one went missing from it.
 */

interface Props {
  file: CityFile | null;
  /** Every listing, with the user's edits applied. */
  byId: ReadonlyMap<string, Opportunity>;
  rsvp: Rsvp;
  hidden: HiddenEvents;
  /** A phone has room for the icon only. */
  round: boolean;
  /** Back up, Restore and Clear only, behind a "…": the Share my calendar dialog's overflow. */
  overflow?: boolean;
  notify: (message: string) => void;
}

export default function SavedMenu({ file, byId, rsvp, hidden, round, overflow, notify }: Props) {
  const city = file?.city ?? 'nyc';
  const cityShort = file?.cityShort ?? 'NYC';

  const downloadCalendar = useCallback(() => {
    const events = [...rsvp.allIds].map((id) => byId.get(id)).filter((e): e is Opportunity => Boolean(e));
    downloadIcs(
      buildIcs({
        scheduled: events.filter((e) => rsvp.statusOf(e.id) === 'going'),
        saved: events.filter((e) => rsvp.statusOf(e.id) === 'maybe'),
        calendarName: `CommonGround ${cityShort}`,
        timezone: file?.timezone,
      }),
      `commonground-${city}.ics`,
    );
    notify(`Downloaded ${events.length} ${events.length === 1 ? 'event' : 'events'} as commonground-${city}.ics.`);
  }, [byId, rsvp, cityShort, file?.timezone, city, notify]);

  const takeBackup = useCallback(() => makeBackup(city, rsvp.map, hidden.ids), [city, rsvp.map, hidden.ids]);

  const restore = useCallback(
    (backup: Backup) => {
      if (backup.city !== city) {
        notify(`That backup is for ${backup.city.toUpperCase()}, not ${cityShort}.`);
        return;
      }
      const merged = mergeBackup({ rsvp: rsvp.map, hidden: hidden.ids }, backup);
      rsvp.replace(merged.rsvp);
      const keep = new Set(merged.hidden);
      for (const id of merged.hidden) if (!hidden.isHidden(id)) hidden.hide(id);
      for (const id of hidden.ids) if (!keep.has(id)) hidden.unhide(id);
      notify(
        merged.added
          ? `Restored: ${merged.added} ${merged.added === 1 ? 'event' : 'events'} added to Saved.`
          : 'Nothing new in that backup: everything in it is already here.',
      );
    },
    [city, cityShort, rsvp, hidden, notify],
  );

  return (
    <ScheduleMenu
      count={rsvp.allIds.size}
      cityLabel={cityShort}
      round={round}
      overflow={overflow}
      makeBackup={takeBackup}
      onDownloadIcs={downloadCalendar}
      onDownloadBackup={(b) => downloadFile(backupJson(b), `commonground-${city}-backup.json`, 'application/json')}
      onRestore={restore}
      onClear={() => {
        rsvp.clear();
        notify('Saved cleared.');
      }}
      onCopied={(ok) => notify(ok ? 'Backup copied.' : "Couldn't copy. Select the line and copy it yourself.")}
    />
  );
}
